import { Menu, Transition } from "@headlessui/react";
import clsx from "clsx";
import { Fragment, ReactNode } from "react";

import { ChevronDown } from "@/assets/icons/icons";
import { FocusRing } from "@/components/Layout/BaseLayouts/Footer/FocusRing";
import { useIsMobile } from "@/hooks/useIsMobile";

type CollapsibleProps = {
  id: string;
  label: string;
  children: ReactNode;
};

/**
 * Accordion-style collapsible menu used in the footer.
 * The trigger button is styled as an inverted NavItem (xl size) with a
 * rotating chevron-down icon.
 */
export const Collapsible = ({ id, label, children }: CollapsibleProps) => {
  const isMobile = useIsMobile();

  return (
    <Menu
      as="div"
      data-testid={`collapsible-${id}`}
      className="inline-block w-full text-left"
    >
      {({ open }) => (
        <>
          <Menu.Button
            className={clsx(
              "group relative flex items-center gap-2 rounded-md p-1",
              "text-lg font-semibold text-foreground-primary-inverse",
              "hover:bg-surface-secondary-inverse active:bg-surface-secondary-inverse",
              "focus:outline-none",
            )}
            aria-label={label}
          >
            <span className="whitespace-nowrap font-semibold">{label}</span>
            <ChevronDown
              className={clsx(
                "shrink-0 stroke-current transition ease-linear",
                open && "rotate-180",
              )}
            />
            <FocusRing inverted />
          </Menu.Button>
          <Transition
            as={Fragment}
            enter="transition-[max-height,opacity] ease-out duration-300"
            enterFrom="max-h-0 opacity-0"
            enterTo="max-h-96 opacity-100"
            leave="transition-[max-height,opacity] ease-in duration-200"
            leaveFrom="max-h-96 opacity-100"
            leaveTo="max-h-0 opacity-0"
          >
            <Menu.Items
              id={`collapsible-menu-${id}`}
              className={clsx(
                "mt-1 flex flex-col gap-1 focus:outline-none",
                isMobile && "w-screen",
              )}
              static
            >
              {children}
            </Menu.Items>
          </Transition>
        </>
      )}
    </Menu>
  );
};
